import { useCallback, useEffect, useRef } from "react";

import { css } from "~/styled-system/css";

interface Props {
  speed?: number;
  scale?: number;
  dotSize?: number;
  dotGap?: number;
}

export function NoiseGrid({ speed = 0.5, scale = 4.0, dotSize = 1.5, dotGap = 8 }: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const glRef = useRef<WebGLRenderingContext | null>(null);
  const programRef = useRef<WebGLProgram | null>(null);
  const timeRef = useRef<number>(0);
  const fadeRef = useRef<number>(0); // Track fade state (0 to 1)
  const fadeTargetRef = useRef<number>(0); // Target fade state
  const mousePosRef = useRef<[number, number] | null>(null); // Store mouse position

  const updatePosition = useCallback((clientX: number, clientY: number) => {
    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }

    const rect = canvas.getBoundingClientRect();
    const x = clientX - rect.left;
    const y = rect.height - (clientY - rect.top);

    const normalizedX = x / rect.width;
    const normalizedY = y / rect.height;

    mousePosRef.current = [normalizedX, normalizedY];
    fadeTargetRef.current = 1;
  }, []);

  const handleMouseMove = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>) => {
      updatePosition(e.clientX, e.clientY);
    },
    [updatePosition]
  );

  const handleMouseLeave = useCallback(() => {
    // Don't clear mousePosRef on leave to maintain the last position for fade out
    fadeTargetRef.current = 0;
  }, []);

  const handleTouchMove = useCallback(
    (e: TouchEvent) => {
      if (e.cancelable) {
        e.preventDefault();
      }
      if (e.touches.length === 0) {
        return;
      }
      const touch = e.touches[0];
      updatePosition(touch.clientX, touch.clientY);
    },
    [updatePosition]
  );

  const handleTouchEnd = useCallback((e: TouchEvent) => {
    if (e.cancelable) {
      e.preventDefault();
    }
    // Only set the target fade to 0, allowing smooth transition
    fadeTargetRef.current = 0;
    // Don't clear mousePosRef immediately to maintain last position during fade
  }, []);

  // Add touch event listeners with non-passive option
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }

    const touchEndHandler = (e: TouchEvent) => {
      handleTouchEnd(e);
    };

    // Add touch event listeners with { passive: false }
    canvas.addEventListener("touchmove", handleTouchMove, { passive: false });
    canvas.addEventListener("touchend", touchEndHandler, { passive: false });
    canvas.addEventListener("touchcancel", touchEndHandler, { passive: false });

    return () => {
      // Clean up event listeners
      canvas.removeEventListener("touchmove", handleTouchMove);
      canvas.removeEventListener("touchend", touchEndHandler);
      canvas.removeEventListener("touchcancel", touchEndHandler);
    };
  }, [handleTouchMove, handleTouchEnd]);

  // Add cleanup effect to clear mouse position after fade completes
  useEffect(() => {
    if (fadeRef.current === 0 && mousePosRef.current !== null) {
      mousePosRef.current = null;
    }
  }, [fadeRef.current]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }

    const gl = canvas.getContext("webgl");
    if (!gl) {
      console.error("WebGL not supported");
      return;
    }
    glRef.current = gl;

    // Create shader program
    const vertexShader = gl.createShader(gl.VERTEX_SHADER);
    const fragmentShader = gl.createShader(gl.FRAGMENT_SHADER);
    if (!vertexShader || !fragmentShader) {
      return;
    }

    // Simplified vertex shader
    const vertexSource = `
      attribute vec2 a_position;
      void main() {
        gl_Position = vec4(a_position, 0.0, 1.0);
      }
    `;

    // Cleaned up fragment shader
    const fragmentSource = `
      precision highp float;
      uniform float u_time;
      uniform float u_scale;
      uniform vec2 u_resolution;
      uniform vec2 u_mouse;
      uniform float u_fade;
      
      // Optimized simplex noise functions
      vec3 mod289(vec3 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
      vec4 mod289(vec4 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
      vec4 permute(vec4 x) { return mod289(((x*34.0)+1.0)*x); }
      vec4 taylorInvSqrt(vec4 r) { return 1.79284291400159 - 0.85373472095314 * r; }
      
      float snoise(vec3 v) {
        const vec2 C = vec2(1.0/6.0, 1.0/3.0);
        const vec4 D = vec4(0.0, 0.5, 1.0, 2.0);
        
        vec3 i  = floor(v + dot(v, C.yyy));
        vec3 x0 = v - i + dot(i, C.xxx);
        
        vec3 g = step(x0.yzx, x0.xyz);
        vec3 l = 1.0 - g;
        vec3 i1 = min(g.xyz, l.zxy);
        vec3 i2 = max(g.xyz, l.zxy);
        
        vec3 x1 = x0 - i1 + C.xxx;
        vec3 x2 = x0 - i2 + C.yyy;
        vec3 x3 = x0 - D.yyy;
        
        i = mod289(i);
        vec4 p = permute(permute(permute(
          i.z + vec4(0.0, i1.z, i2.z, 1.0))
          + i.y + vec4(0.0, i1.y, i2.y, 1.0))
          + i.x + vec4(0.0, i1.x, i2.x, 1.0));
          
        float n_ = 0.142857142857;
        vec3 ns = n_ * D.wyz - D.xzx;
        
        vec4 j = p - 49.0 * floor(p * ns.z * ns.z);
        
        vec4 x_ = floor(j * ns.z);
        vec4 y_ = floor(j - 7.0 * x_);
        
        vec4 x = x_ *ns.x + ns.yyyy;
        vec4 y = y_ *ns.x + ns.yyyy;
        vec4 h = 1.0 - abs(x) - abs(y);
        
        vec4 b0 = vec4(x.xy, y.xy);
        vec4 b1 = vec4(x.zw, y.zw);
        
        vec4 s0 = floor(b0)*2.0 + 1.0;
        vec4 s1 = floor(b1)*2.0 + 1.0;
        vec4 sh = -step(h, vec4(0.0));
        
        vec4 a0 = b0.xzyw + s0.xzyw*sh.xxyy;
        vec4 a1 = b1.xzyw + s1.xzyw*sh.zzww;
        
        vec3 p0 = vec3(a0.xy, h.x);
        vec3 p1 = vec3(a0.zw, h.y);
        vec3 p2 = vec3(a1.xy, h.z);
        vec3 p3 = vec3(a1.zw, h.w);
        
        vec4 norm = taylorInvSqrt(vec4(dot(p0,p0), dot(p1,p1), dot(p2,p2), dot(p3,p3)));
        p0 *= norm.x;
        p1 *= norm.y;
        p2 *= norm.z;
        p3 *= norm.w;
        
        vec4 m = max(0.6 - vec4(dot(x0,x0), dot(x1,x1), dot(x2,x2), dot(x3,x3)), 0.0);
        m = m * m;
        return 42.0 * dot(m*m, vec4(dot(p0,x0), dot(p1,x1), dot(p2,x2), dot(p3,x3)));
      }
      
      // Simplified dot function
      float dot(vec2 uv, float size) {
        return smoothstep(size, size - 0.1, length(uv - vec2(0.5)));
      }
      
      void main() {
        // Calculate coordinates and aspect ratio
        vec2 uv = gl_FragCoord.xy / u_resolution;
        float aspect = u_resolution.x / u_resolution.y;
        vec2 aspectUV = vec2(uv.x * aspect, uv.y);
        
        // Generate noise
        float noise = snoise(vec3(uv * u_scale * vec2(aspect, 1.0), u_time * float(${speed}))) * 0.5 + 0.5;
        
        // Generate base color
        vec3 noiseColor = mix(
          vec3(0.02, 0.02, 0.02),
          vec3(0.6, 0.3, 1.0),
          pow(noise, 0.7)
        );
        
        // Calculate dot grid
        vec2 dotUV = fract(gl_FragCoord.xy / float(${dotGap}));
        float dotMask = dot(dotUV, float(${dotSize}) / float(${dotGap}));
        
        // Ambient wave effect
        float ambientWave = sin(u_time * 0.5) * 0.5 + 0.5;
        float ambientIntensity = mix(0.35, 0.55, ambientWave);
        
        // Mouse interaction
        vec2 aspectMouse = vec2(u_mouse.x * aspect, u_mouse.y);
        float dist = length(aspectUV - aspectMouse);
        
        // Flashlight effect with fixed radius
        float baseRadius = 0.15; // Base radius
        float innerRadius = baseRadius * 0.33; // Inner radius is 1/3 of outer
        float outerRadius = baseRadius;
        float flashlight = pow(smoothstep(outerRadius, innerRadius, dist), 2.0);
        float distanceFade = smoothstep(outerRadius * 1.5, 0.0, dist);
        
        // Combine effects
        float enhancedDotMask = dotMask * mix(
          ambientIntensity,
          mix(ambientIntensity, 1.0, distanceFade),
          u_fade
        );
        
        // Final color calculation
        vec3 finalColor = mix(vec3(0.0), noiseColor, enhancedDotMask) * 1.25;
        
        // Apply flashlight
        if (u_fade > 0.0) {
          finalColor = mix(
            finalColor,
            finalColor * 2.5,
            flashlight * u_fade
          );
        }
        
        gl_FragColor = vec4(finalColor, 1.0);
      }
    `;

    // Compile shaders
    gl.shaderSource(vertexShader, vertexSource);
    gl.shaderSource(fragmentShader, fragmentSource);
    gl.compileShader(vertexShader);
    gl.compileShader(fragmentShader);

    // Check for shader compilation errors
    if (!gl.getShaderParameter(vertexShader, gl.COMPILE_STATUS)) {
      console.error("Vertex shader compilation error:", gl.getShaderInfoLog(vertexShader));
      return;
    }
    if (!gl.getShaderParameter(fragmentShader, gl.COMPILE_STATUS)) {
      console.error("Fragment shader compilation error:", gl.getShaderInfoLog(fragmentShader));
      return;
    }

    // Create and link program
    const program = gl.createProgram();
    if (!program) {
      return;
    }
    programRef.current = program;

    gl.attachShader(program, vertexShader);
    gl.attachShader(program, fragmentShader);
    gl.linkProgram(program);

    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
      console.error("Program linking error:", gl.getProgramInfoLog(program));
      return;
    }

    // Create a buffer for the square that fills the canvas
    const positionBuffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, positionBuffer);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW);

    // Get attribute and uniform locations
    const positionLocation = gl.getAttribLocation(program, "a_position");
    const timeLocation = gl.getUniformLocation(program, "u_time");
    const scaleLocation = gl.getUniformLocation(program, "u_scale");
    const resolutionLocation = gl.getUniformLocation(program, "u_resolution");
    const mouseLocation = gl.getUniformLocation(program, "u_mouse");
    const fadeLocation = gl.getUniformLocation(program, "u_fade");

    // Animation loop
    let animationFrameId: number;
    const render = () => {
      const gl = glRef.current;
      const program = programRef.current;
      if (!gl || !program) {
        return;
      }

      // Update canvas size
      const displayWidth = canvas.clientWidth;
      const displayHeight = canvas.clientHeight;
      if (canvas.width !== displayWidth || canvas.height !== displayHeight) {
        canvas.width = displayWidth;
        canvas.height = displayHeight;
        gl.viewport(0, 0, displayWidth, displayHeight);
      }

      // Clear the canvas with transparency
      gl.clearColor(0, 0, 0, 0);
      gl.clear(gl.COLOR_BUFFER_BIT);

      // Use our shader program
      gl.useProgram(program);

      // Set up the position attribute
      gl.bindBuffer(gl.ARRAY_BUFFER, positionBuffer);
      gl.enableVertexAttribArray(positionLocation);
      gl.vertexAttribPointer(positionLocation, 2, gl.FLOAT, false, 0, 0);

      // Update fade transition
      const fadeSpeed = 0.05; // Adjust this to change transition speed
      if (fadeRef.current < fadeTargetRef.current) {
        fadeRef.current = Math.min(fadeRef.current + fadeSpeed, fadeTargetRef.current);
      } else if (fadeRef.current > fadeTargetRef.current) {
        fadeRef.current = Math.max(fadeRef.current - fadeSpeed, fadeTargetRef.current);
      }

      // Update uniforms
      timeRef.current += 0.01;
      gl.uniform1f(timeLocation, timeRef.current);
      gl.uniform1f(scaleLocation, scale);
      gl.uniform2f(resolutionLocation, displayWidth, displayHeight);
      gl.uniform1f(fadeLocation, fadeRef.current);

      // Use mouse position directly, it will be null during fade out
      const currentMousePos = mousePosRef.current || [0, 0];
      gl.uniform2f(mouseLocation, currentMousePos[0], currentMousePos[1]);

      // Draw the square
      gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);

      // Continue the animation loop
      animationFrameId = requestAnimationFrame(render);
    };

    // Start the animation loop
    render();

    // Cleanup
    return () => {
      cancelAnimationFrame(animationFrameId);
      gl.deleteProgram(program);
      gl.deleteShader(vertexShader);
      gl.deleteShader(fragmentShader);
    };
  }, [speed, scale, dotSize, dotGap]);

  return (
    <div className={containerStyles}>
      <canvas
        ref={canvasRef}
        className={canvasStyles}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        onContextMenu={(e) => e.preventDefault()}
      />
    </div>
  );
}

const containerStyles = css({
  position: "absolute",
  inset: 0,
  overflow: "hidden",
  zIndex: 0,
  backgroundColor: "black",
  userSelect: "none",
  WebkitUserSelect: "none",
  WebkitTouchCallout: "none",
});

const canvasStyles = css({
  width: "100%",
  height: "100%",
  display: "block",
  userSelect: "none",
  WebkitUserSelect: "none",
  WebkitTouchCallout: "none",
  "&::context-menu": {
    display: "none",
  },
});
